import { ChangeDetectionStrategy, Component, inject, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MIN_MONTH, MIN_YEAR } from '@app/shared/constants';
import { MONTH_LABELS, Months } from '@enum/months';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap/tooltip';

import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'abc-month-year-selector',
  imports: [FormsModule, NgbTooltipModule],
  templateUrl: 'month-year-selector.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthYearSelector {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected Months = Months;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  protected MONTH_LABELS = MONTH_LABELS;

  private toastrService = inject(ToastrService);

  month = model.required<number>();
  year = model.required<number>();

  pendingChanges = input<boolean>(false);

  isMonthDisabled(month: number): boolean {
    return this.year() === MIN_YEAR && month < MIN_MONTH;
  }

  onMonthChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    if (this.pendingChanges()) {
      this.triggerUnsavedChangesAlert();
      target.value = this.month().toString();
      return;
    }

    const newMonth = Number(target.value);
    if (this.year() === MIN_YEAR && newMonth < MIN_MONTH) {
      this.triggerMinMonthAlert();
      target.value = this.month().toString();
      return;
    }

    this.month.set(newMonth);
  }

  onYearChange(event: Event) {
    const target = event.target as HTMLInputElement;

    if (this.pendingChanges()) {
      this.triggerUnsavedChangesAlert();
      target.value = this.year().toString();
      return;
    }

    const newYear = Number(target.value);
    if (newYear < MIN_YEAR) {
      this.triggerMinMonthAlert();
      target.value = this.year().toString();
      return;
    }

    this.year.set(newYear);

    // If the user enters MIN_YEAR while sitting in a month below MIN_MONTH,
    // bump the month up to the floor so we never end up below the minimum.
    if (newYear === MIN_YEAR && this.month() < MIN_MONTH) {
      this.month.set(MIN_MONTH);
    }
  }

  previousMonth() {
    if (this.pendingChanges()) {
      this.triggerUnsavedChangesAlert();
      return;
    }

    if (!this.canGoPrevious()) {
      this.triggerMinMonthAlert();
      return;
    }

    if (this.month() === 1) {
      this.month.set(12);
      this.year.update((value) => value - 1);
    } else {
      this.month.update((value) => value - 1);
    }
  }

  nextMonth() {
    if (this.pendingChanges()) {
      this.triggerUnsavedChangesAlert();
      return;
    }

    if (this.month() === 12) {
      this.month.set(1);
      this.year.update((value) => value + 1);
    } else {
      this.month.update((value) => value + 1);
    }
  }

  goToPresent() {
    if (this.pendingChanges()) {
      this.triggerUnsavedChangesAlert();
      return;
    }

    this.year.set(new Date().getFullYear());
    this.month.set(new Date().getMonth() + 1);
  }

  triggerUnsavedChangesAlert() {
    this.toastrService.warning(
      $localize`:toast|Warning message for unsaved changes before changing months@@unsavedChangesWarning:Please save your changes before switching months.`
    );
  }

  private triggerMinMonthAlert() {
    this.toastrService.warning(
      $localize`:toast|Warning message for attempting to navigate before the minimum allowed month@@minMonthWarning:Cannot navigate before application introduction date.`
    );
  }

  private canGoPrevious(): boolean {
    return this.year() > MIN_YEAR || (this.year() === MIN_YEAR && this.month() > MIN_MONTH);
  }
}
